import { ChevronLeft } from 'lucide-react';
import { useNavigate } from 'react-router-dom';
import { useIntl } from '@/core/i18n/use-intl';
import { Button } from '@/shared/components/ui';
import type { ButtonProps } from '@/shared/components/ui/button';

type Action = ButtonProps & {
  label: string;
  loadingLabel?: string;
  isLoading?: boolean;
};

interface PageHeaderProps {
  title: string | React.ReactNode;
  subtitle?: string;
  backHref?: string;
  primaryAction?: Action;
  secondaryAction?: Action;
  actions?: React.ReactNode;
}

export function PageHeader({
  title,
  subtitle,
  backHref,
  primaryAction,
  secondaryAction,
  actions,
}: PageHeaderProps) {
  const { t } = useIntl();
  const navigate = useNavigate();

  const getButtonProps = (action: Action) => {
    const { loadingLabel: _loadingLabel, isLoading: _isLoading, ...buttonProps } = action;
    return buttonProps;
  };

  const handleBack = () => {
    // Check if there's history to go back to
    if (window.history.length > 1) {
      navigate(-1);
    } else if (backHref) {
      // Fallback to the provided backHref if no history
      navigate(backHref);
    }
  };

  return (
    <div className="sticky top-0 z-10 -mx-6 -mt-6 mb-6 backdrop-blur-xs">
      <div className="bg-background/80 px-6 py-4">
        <div className="flex items-center gap-4 h-9">
          {backHref && (
            <Button variant="outline" size="icon" className="size-7" onClick={handleBack}>
              <ChevronLeft className="size-4" />
              <span className="sr-only">{t('common.back')}</span>
            </Button>
          )}

          <div className="flex-1 shrink-0 flex items-center gap-3">
            {typeof title === 'string' ? (
              <h1 className="whitespace-nowrap text-xl font-semibold tracking-tight">{title}</h1>
            ) : (
              <h1 className="whitespace-nowrap text-xl font-semibold tracking-tight">{title}</h1>
            )}
            {subtitle && <p className="text-sm text-muted-foreground">{subtitle}</p>}
          </div>

          {actions ||
            ((primaryAction || secondaryAction) && (
              <div className="flex items-center gap-2">
                {secondaryAction && (
                  <Button
                    {...getButtonProps(secondaryAction)}
                    variant={secondaryAction.variant || 'outline-solid'}
                  >
                    {secondaryAction.isLoading
                      ? secondaryAction.loadingLabel
                      : secondaryAction.label}
                  </Button>
                )}
                {primaryAction && (
                  <Button
                    {...getButtonProps(primaryAction)}
                    variant={primaryAction.variant || 'default'}
                  >
                    {primaryAction.isLoading ? primaryAction.loadingLabel : primaryAction.label}
                  </Button>
                )}
              </div>
            ))}
        </div>
      </div>
    </div>
  );
}
